import { motion } from "framer-motion";
import { Browser, DeviceMobile, Sparkle, ArrowUpRight } from "@phosphor-icons/react";
import { Reveal, MaskLine, Parallax, scrollToId } from "@/lib/anim";

const ACCENT = {
  teal: { chip: "text-teal", ring: "hover:border-teal/50", glow: "bg-teal/10 group-hover:bg-teal/25", icon: "bg-teal/10 border-teal/25 text-teal", bar: "from-teal to-cobalt" },
  cobalt: { chip: "text-cobalt", ring: "hover:border-cobalt/50", glow: "bg-cobalt/10 group-hover:bg-cobalt/25", icon: "bg-cobalt/15 border-cobalt/30 text-cobalt", bar: "from-cobalt to-violet" },
  violet: { chip: "text-violet", ring: "hover:border-violet/50", glow: "bg-violet/10 group-hover:bg-violet/25", icon: "bg-violet/15 border-violet/30 text-violet", bar: "from-violet to-magenta" },
};

const Shot = ({ src, alt }) => (
  <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black/40 group/shot">
    <motion.img
      initial={{ scale: 1.08, opacity: 0, filter: "blur(8px)" }}
      whileInView={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      src={src}
      alt={alt}
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
      loading="lazy"
    />
    {/* shine sweep on hover */}
    <div className="pointer-events-none absolute -inset-y-6 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-0 group-hover:translate-x-[320%] transition-transform duration-[1100ms] ease-out" />
    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5 rounded-xl" />
  </div>
);

const IconTile = ({ Icon, className }) => (
  <motion.div
    animate={{ y: [0, -6, 0] }}
    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    whileHover={{ rotate: -8, scale: 1.08 }}
    className={`rounded-2xl border flex items-center justify-center ${className}`}
  >
    <Icon size={26} weight="duotone" />
  </motion.div>
);

export const Services = () => {
  return (
    <section
      id="services"
      className="relative py-28 md:py-40 bg-black/20"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <Parallax speed={0.12} className="max-w-2xl">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] text-teal/80">
                What we build
              </span>
            </Reveal>
            <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-white text-3xl sm:text-4xl md:text-5xl lg:text-[3.75rem] leading-[1.02]">
              <MaskLine>Three ways to</MaskLine>
              <MaskLine delay={0.08}>build it your way.</MaskLine>
            </h2>
          </Parallax>
          <Reveal delay={1}>
            <button
              onClick={() => scrollToId("#contact")}
              className="group inline-flex items-center gap-2 text-white/80 border border-white/20 rounded-full px-6 py-3 hover:bg-white/10 transition-colors duration-300"
              data-testid="services-cta"
            >
              See what we build
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
          </Reveal>
        </div>

        <div className="mt-16 space-y-6">
          {/* WEBSITES — full-width showcase */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className={`group relative rounded-3xl border border-white/10 bg-[#0A0A12]/80 backdrop-blur-sm overflow-hidden transition-colors duration-500 ${ACCENT.teal.ring}`}
            data-testid="service-card-websites"
          >
            <div className={`absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl transition-colors duration-700 ${ACCENT.teal.glow}`} />
            <div className="relative grid md:grid-cols-2 gap-8 p-8 md:p-10 items-center">
              <div>
                <div className="flex items-center gap-4">
                  <IconTile Icon={Browser} className={`h-14 w-14 ${ACCENT.teal.icon}`} />
                  <span className="text-xs uppercase tracking-[0.2em] text-white/40">Websites</span>
                </div>
                <h3 className="mt-7 font-heading font-bold text-white text-3xl md:text-4xl leading-tight">
                  The website you've always wanted
                </h3>
                <p className="mt-4 text-white/60 leading-relaxed max-w-md">
                  Exactly how you envisioned it. No theme limitations. No "our template doesn't do that." If you can describe it, we can build it — with AI automations baked right in.
                </p>
                <ul className="mt-7 flex flex-wrap gap-2">
                  {["Pixel-perfect, on-brand", "Fast & SEO-ready", "Automations built in"].map((p) => (
                    <li key={p} className="text-xs text-white/70 border border-white/15 rounded-full px-3 py-1.5">{p}</li>
                  ))}
                </ul>
              </div>
              <Shot src="/services/web.jpg" alt="Custom website design examples" />
            </div>
          </motion.div>

          {/* APPS + AI — two columns */}
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                accent: "cobalt", icon: DeviceMobile, tag: "Apps", img: "/services/app.jpg",
                title: "Apps that run your business",
                body: "A custom CRM that mirrors your actual sales process. A client portal your customers love. Or that app idea you've been sitting on.",
                points: ["Custom CRMs", "Client portals", "Your app idea, shipped"],
              },
              {
                accent: "violet", icon: Sparkle, tag: "AI Automations", img: "/services/ai.jpg",
                title: "Business that runs while you sleep",
                body: "We design AI automations around your workflows — so the repetitive stuff runs itself and your team focuses on work that grows revenue.",
                points: ["Workflow automation", "AI agents & pipelines", "Less manual work"],
              },
            ].map((s, i) => {
              const a = ACCENT[s.accent];
              return (
                <motion.div
                  key={s.tag}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6 }}
                  className={`group relative rounded-3xl border border-white/10 bg-[#0A0A12]/80 backdrop-blur-sm overflow-hidden flex flex-col transition-colors duration-500 ${a.ring}`}
                  data-testid={`service-card-${s.tag.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className={`absolute -right-20 -top-20 h-52 w-52 rounded-full blur-3xl transition-colors duration-700 ${a.glow}`} />
                  <div className="relative p-6 md:p-8">
                    <Shot src={s.img} alt={`${s.tag} design examples`} />
                    <div className="mt-6 flex items-center gap-3">
                      <IconTile Icon={s.icon} className={`h-12 w-12 ${a.icon}`} />
                      <span className="text-xs uppercase tracking-[0.2em] text-white/40">{s.tag}</span>
                    </div>
                    <h3 className="mt-5 font-heading font-bold text-white text-2xl md:text-3xl leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-white/60 leading-relaxed">{s.body}</p>
                    <ul className="mt-6 flex flex-wrap gap-2">
                      {s.points.map((p) => (
                        <li key={p} className="text-xs text-white/70 border border-white/15 rounded-full px-3 py-1.5">{p}</li>
                      ))}
                    </ul>
                    <div className={`mt-6 h-1 w-0 group-hover:w-full rounded-full bg-gradient-to-r ${a.bar} transition-all duration-500`} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
